import React, { useCallback, useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Radio } from 'antd';
import { useMediaQuery } from 'react-responsive';
import type { RadioChangeEvent } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { isEmpty } from 'lodash';
import { Search, RoundedIcon, Translate } from '../index';

import Icons from '../../../configs/icon.config';
import { openDialog } from '../Large-footer/Large-footer-top/store/stateSlice';
import filterSearch, {
  extractSubMenuItems,
  userFilterSearch,
  MenuProps,
} from '../../../utils/menu_searchfilter-utils';
import { useGetUserByParamsQuery } from './store/employeeSearchSlice';
import UserProfile from '../../template/Profile-modal/UserProfile';
import { useGetSideNavMenusQuery } from '../../Admin/Menu/store/menuAdminSlice';
import { setFeedbackDrawerOpen } from '../Feedback/store/stateSlice';
import './index.scss';

interface SearchNavProps {
  classname?: string;
  styles?: React.CSSProperties;
  onCloseButtonClick: () => void;
  profileSearch: string;
}

const setRecentSearch = (payload: any, key: string) => {
  const availableValues = JSON.parse(localStorage.getItem(key) || '[]');
  const state = availableValues ? [...availableValues] : [];
  // find the index of an existing item
  const isAvailable = state?.findIndex(
    (search: any) => search.label === payload.label
  );
  if (isAvailable !== -1) {
    // remove the option from the existing array
    state.splice(isAvailable, 1);
    // then insert the new item
    state.unshift(payload);
  } else if (state.length === 5) {
    // if array length is > 5 then remove the last item
    state.pop();
    // inser new item
    state.unshift(payload);
  } else {
    state.unshift(payload);
  }

  const recentSearchValue = JSON.stringify(state);
  localStorage.setItem(key, recentSearchValue);
};

export function SearchNav({
  classname,
  styles,
  onCloseButtonClick,
  profileSearch,
}: SearchNavProps) {
  const navigate = useNavigate();
  const avatarStyle = {
    ...styles,
  };
  const dispatch = useDispatch();
  const [options, setOptions] = useState<any>([]);
  const [eployeeSearchOptions, setEployeeSearchOptions] = useState<any>([]);
  const classes = classNames('nav-search', classname);
  const [pin, setPin] = useState();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [selectedSearchItem, setSelectedSearchItem] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchType, setSearchType] = useState('my-brac');
  const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
  const { data: menuData } = useGetSideNavMenusQuery();

  // @ts-ignore
  const menuProps: MenuProps | undefined = menuData;
  const [params, setParam] = useState('');
  const [searchVal, setSearchVal] = useState('');

  const searchDebounce = useDebouncedCallback((value) => {
    setSearchVal(value);
  }, 800);

  const escFunction = useCallback(
    (event: any) => {
      if (event.key === 'Escape') {
        onCloseButtonClick();
      }
    },
    [onCloseButtonClick]
  );

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  // generate the popular search options for myBrac Search ==> task no. AR-734
  useEffect(() => {
    const myBracRecentSearchOptions = JSON.parse(
      localStorage.getItem('myBracRecentSearch') || '{}'
    );

    let recentSearchOptions: any = [];

    if (!isEmpty(myBracRecentSearchOptions)) {
      recentSearchOptions = [
        {
          label: (
            <span className="text-[#9E9E9E] text-2xl">
              <Translate
                en="Your recent search"
                bn="আপনার সাম্প্রতিক অনুসন্ধান"
              />
            </span>
          ),
        },
        ...myBracRecentSearchOptions,
      ];
    }

    const popularMenuItes: any = [];
    // @ts-ignore
    for (let i = 0; i < menuProps?.length; i += 1) {
      for (
        let j = 0;
        // @ts-ignore
        j < menuProps?.[i]?.sub_menu_items.length;
        j += 1
      ) {
        // @ts-ignore
        if (menuProps?.[i]?.sub_menu_items[j].is_popular) {
          // @ts-ignore
          popularMenuItes.push(menuProps?.[i]?.sub_menu_items[j]);
        }
      }
    }

    const popularOptions: any = [];

    for (let i = 0; i < popularMenuItes.length; i += 1) {
      popularOptions.push({
        label: popularMenuItes[i].title_en,
        option: {
          url: popularMenuItes[i].url,
          key: popularMenuItes[i].keywords,
        },
        value: `${popularMenuItes[i].id}-${uuidv4()}`,
      });
      if (i > 4) break;
    }

    const searchOptions = [
      ...recentSearchOptions,
      {
        label: (
          <span className="text-[#9E9E9E] text-2xl">
            <Translate en="Popular search" bn="জনপ্রিয় অনুসন্ধান" />
          </span>
        ),
      },
      ...popularOptions,
    ];
    setOptions(searchOptions);
  }, [menuProps, searchType]);

  const closeUserProfile = useCallback(() => {
    setIsUserProfileOpen(false);
    // dispatch(setProfileModalOpen(false));
  }, []);

  const handleClick = useCallback(() => {
    // Navigate to the desired route when the button is clicked.
    navigate(`user-list/${params}`); // Replace '/results' with the actual route you want to navigate to.
  }, [navigate, params]);

  // const { data: profileData, isFetching } = useGetUserByPinQuery(
  //   { pin },
  //   { skip: !pin }
  // );

  const { data, isFetching: userIsFetching } = useGetUserByParamsQuery(
    { params: searchVal },
    { skip: !(searchVal && searchType === 'user_profiles') }
  );

  const onChange = useCallback((e: RadioChangeEvent) => {
    setSearchType(e.target.value);
    setOptions([]);
    setEployeeSearchOptions([]);
    setSelectedSearchItem('');
  }, []);

  const handleSearchChange = (searchValue: string) => {
    if (!searchValue) {
      setOptions([]);
    }
    searchDebounce(searchValue);
    setSearchInputValue(searchValue);
    setSelectedSearchItem(searchValue);
    if (searchType === 'my-brac') {
      const filterVal = filterSearch(
        extractSubMenuItems(menuProps),
        searchValue
      );

      const generatedOption = filterVal.map((v: any) => {
        return {
          label: v.title_en,
          value: v.title_en,
          option: {
            url: v.url,
            key: v.keywords,
          },
        };
      });

      if (generatedOption.length >= 1) {
        // @ts-ignore
        generatedOption.unshift({
          label: (
            <span className="text-[#9E9E9E] text-2xl">
              <Translate
                en="Found in MyBRAC"
                bn="মাই-ব্র্যাক এ পাওয়া গিয়েছে"
              />
            </span>
          ),
        });
      }
      setOptions(generatedOption);
    } else if (searchType === 'user_profiles') {
      setParam(searchValue);
    }
  };

  const userSearch = useMemo(() => {
    let tempData = [];

    if (!isEmpty(data)) {
      if (Array.isArray(data)) {
        tempData = data;
      } else {
        tempData = [data];
      }
    }

    function formatBengaliYear(year: any) {
      const bengaliNumerals = [
        '০',
        '১',
        '২',
        '৩',
        '৪',
        '৫',
        '৬',
        '৭',
        '৮',
        '৯',
      ];

      const yearString = year.toString();
      let formattedYear = '';

      for (let i = 0; i < yearString.length; i++) {
        const digit = parseInt(yearString[i], 10);
        formattedYear += bengaliNumerals[digit];
      }

      return formattedYear;
    }

    const filterVal = userFilterSearch(tempData, params);
    const generatedOption = filterVal?.slice(0, 4)?.map((v: any) => {
      return {
        label: `${v.StaffName} (${v.ProjectName.slice(0, 40)})`,
        value: `${v.StaffName} (${v.ProjectName.slice(0, 40)})`,
        option: {
          url: v.url,
          key: v.StaffPIN,
        },
      };
    });

    if (generatedOption?.length >= 1) {
      // @ts-ignore
      generatedOption.unshift({
        // @ts-ignore
        label: (
          <span className="text-[#9E9E9E] text-2xl">
            <Translate
              en="Found in BRAC Employees"
              bn="ব্র্যাক কর্মীদের মধ্যে পাওয়া গিয়েছে"
            />
          </span>
        ),
      });
    }

    if (generatedOption?.length > 3) {
      // @ts-ignore
      generatedOption.push({
        // @ts-ignore
        label: (
          <button
            className="flex text-pink-500 w-full underline items-center text-2xl"
            onClick={handleClick}
          >
            <span className="mr-1">{Icons.eye}</span>
            <Translate en="See all " bn={`"${params}" এর জন্য`} />
            &nbsp;
            <Translate
              en={`${data?.length} `}
              bn={`${formatBengaliYear(data?.length)} `}
            />
            &nbsp;
            <Translate
              en={`Results for
              "${params}"`}
              bn="ফলাফলের মধ্যে  সব দেখুন "
            />
          </button>
        ),
        value: `${params}`,
        option: {
          url: `/user-list/${params}`,
          key: 'user-list',
        },
      });
    }
    return generatedOption;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, params]);

  useEffect(() => {
    setEployeeSearchOptions(userSearch);
  }, [data, params, userSearch]);

  // generate the recent search options for employee search ==> task no. AR-734
  useEffect(() => {
    const employeeRecentSearchOptions = JSON.parse(
      localStorage.getItem('employeeRecentSearch') || '{}'
    );

    if (!isEmpty(employeeRecentSearchOptions)) {
      const recentSearchOptions = [
        {
          label: (
            <span className="text-[#9E9E9E] text-2xl">
              <Translate
                en="Your recent search"
                bn="আপনার সাম্প্রতিক অনুসন্ধান"
              />
            </span>
          ),
        },
        ...employeeRecentSearchOptions,
      ];
      setEployeeSearchOptions(recentSearchOptions);
    }
  }, [searchType]);

  const handleSelect = useCallback(
    (value: any) => {
      if (searchType === 'my-brac') {
        // set value to the redux state for recent search options. ==> task no. AR-734
        // dispatch(setRecentSearch(value));
        setRecentSearch(value, 'myBracRecentSearch');

        if (value.option.key.includes('{Lv]$ldw>U]t2fy')) {
          dispatch(openDialog());
        } else if (value.option.key.includes('{Lv]$ldw>U]t3fy')) {
          dispatch(setFeedbackDrawerOpen(true));
        } else {
          const isExternalURL = /^https?:\/\//.test(value.option.url);
          if (isExternalURL) {
            window.open(value.option.url, '_blank');
          } else {
            navigate(value.option.url);
          }
        }
      } else if (value?.option?.key === 'user-list') {
        navigate(value?.option?.url);
      } else {
        // set value to the redux state for recent search options. ==> task no. AR-734
        if (!isEmpty(value)) {
          setRecentSearch(value, 'employeeRecentSearch');
        }
        const userPin = value?.option?.key;
        setPin(userPin);
        if (!isEmpty(userPin)) {
          setIsUserProfileOpen(true);
          // dispatch(setProfileModalOpen(true));
        }
      }
      setSelectedSearchItem(value?.label);
    },
    [dispatch, navigate, searchType]
  );

  useEffect(() => {
    setSearchType(profileSearch);
  }, [profileSearch]);

  return (
    <>
      <div className={classes} style={avatarStyle}>
        <nav className="nav-bar">
          <div className="nav-container order-2 md:order-1">
            <div className="nav-brand">
              <Search
                onSelect={handleSelect}
                options={
                  searchType === 'my-brac' ? options : eployeeSearchOptions
                }
                classname="bracSearch"
                value={selectedSearchItem}
                width={isMobile ? 300 : 670}
                listHeight={isMobile ? 300 : 350}
                onSearch={handleSearchChange}
                placeholder={
                  <Translate
                    en="Enter keywords to search"
                    bn="খোঁজার জন্য কীওয়ার্ড লিখুন"
                  />
                }
                loading={userIsFetching || searchDebounce.isPending()}
                foundContent={searchInputValue.length > 0}
              />
            </div>
          </div>
          <div className="nav-avatar order-1 md:order-2">
            <div className="nav-check">
              <Radio.Group
                className="flex flex-row  md:flex-col gap-2 md:gap-4"
                style={{ width: '100%' }}
                onChange={onChange}
                value={searchType}
              >
                <Radio className="text-lg md:text-xl" value="my-brac">
                  <Translate en="Search MyBRAC" bn="মাই-ব্র্যাকে খুঁজুন" />
                </Radio>
                <Radio className="text-lg md:text-xl" value="user_profiles">
                  <Translate
                    en="Search BRAC Employees"
                    bn="ব্র্যাক কর্মীদের খুঁজুন"
                  />
                </Radio>
              </Radio.Group>
            </div>
            {!isMobile ? (
              <RoundedIcon
                iconName={Icons.cross}
                classname="cancel"
                width={35}
                height={35}
                onClick={onCloseButtonClick}
                styles={{ color: '#D10D74', fontSize: 30 }}
              />
            ) : (
              <button
                className="text-lg underline text-pink-800"
                onClick={onCloseButtonClick}
              >
                <Translate en="CLOSE" bn="ক্লোস" />
              </button>
            )}
          </div>
        </nav>
      </div>
      {/* {isFetching && isUserProfileOpen ? (
        <Loading />
      ) : ( */}
      <UserProfile
        other
        pin={pin}
        visible={isUserProfileOpen}
        onClose={closeUserProfile}
        // loading={isFetching}
      />
      {/* )} */}
    </>
  );
}

export default React.memo(SearchNav);
